import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Star,
  StarBorder,
  Search,
  Refresh,
  Visibility,
  Edit,
  Delete,
  Add,
  FilterList,
  Sort
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { productService } from '../../services/productService';
import { getProductPrimaryImageUrl } from '../../utils/imageUtils';

const FeaturedProductsTab = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  
  // State management
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [addFeaturedDialogOpen, setAddFeaturedDialogOpen] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Fetch featured products
  const fetchFeaturedProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getFeaturedProducts();
      setFeaturedProducts(response || []);
    } catch (error) {
      console.error('Error fetching featured products:', error);
      toast.error('Failed to fetch featured products');
    } finally {
      setLoading(false);
    }
  };

  // Fetch all approved products for selection
  const fetchAllProducts = async () => {
    try {
      const response = await productService.getAllProducts({ status: 'APPROVED', page: 0, size: 100 });
      const products = response?.content || response || [];
      setAllProducts(products);
      setFilteredProducts(products);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to fetch products');
    }
  };

  // Toggle featured status
  const toggleFeaturedStatus = async (productId, currentStatus) => {
    try {
      await productService.setProductFeatured(productId, !currentStatus);

      toast.success(currentStatus ? 'Product removed from featured' : 'Product added to featured');
      fetchFeaturedProducts();
      fetchAllProducts();
    } catch (error) {
      console.error('Error toggling featured status:', error);
      toast.error('Failed to update featured status');
    }
  };

  // Filter products based on search term
  useEffect(() => {
    if (searchTerm) {
      const filtered = allProducts.filter(product =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sellerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(allProducts);
    }
  }, [searchTerm, allProducts]);

  // Load data on component mount
  useEffect(() => {
    fetchFeaturedProducts();
    fetchAllProducts();
  }, []);

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setProductDialogOpen(true);
  };

  const getProductImageUrl = (product) => {
    return getProductPrimaryImageUrl(product, false, 'thumbnail') || '/placeholder-product.jpg';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Trending Products Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage which products appear in the trending section on the homepage
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchFeaturedProducts();
              fetchAllProducts();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddFeaturedDialogOpen(true)}
          >
            Add Featured Product
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <Star />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {featuredProducts.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Trending Products
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <Visibility />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {allProducts.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Available Products
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <FilterList />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {((featuredProducts.length / Math.max(allProducts.length, 1)) * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Featured Ratio
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Current Featured Products */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Star color="primary" />
            Current Trending Products
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : featuredProducts.length === 0 ? (
            <Alert severity="info">
              No featured products currently selected. Add some products to the featured section.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Seller</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {featuredProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={getProductImageUrl(product)}
                            variant="rounded"
                            sx={{ width: 50, height: 50 }}
                          />
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {product.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {product.id}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{product.category || 'N/A'}</TableCell>
                      <TableCell>{product.sellerName || 'N/A'}</TableCell>
                      <TableCell>₹{product.price?.toLocaleString() || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label="Featured"
                          color="primary"
                          size="small"
                          icon={<Star />}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewProduct(product)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove from Featured">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => toggleFeaturedStatus(product.id, true)}
                            >
                              <StarBorder />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add Featured Product Dialog */}
      <Dialog
        open={addFeaturedDialogOpen}
        onClose={() => setAddFeaturedDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Product to Featured</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Featured</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => {
                  const isFeatured = featuredProducts.some(fp => fp.id === product.id);
                  return (
                    <TableRow key={product.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={getProductImageUrl(product)}
                            variant="rounded"
                            sx={{ width: 40, height: 40 }}
                          />
                          <Box>
                            <Typography variant="subtitle2">
                              {product.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {product.sellerName}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{product.category || 'N/A'}</TableCell>
                      <TableCell>₹{product.price?.toLocaleString() || 'N/A'}</TableCell>
                      <TableCell>
                        {isFeatured ? (
                          <Chip label="Featured" color="primary" size="small" icon={<Star />} />
                        ) : (
                          <Chip label="Not Featured" variant="outlined" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant={isFeatured ? "outlined" : "contained"}
                          color={isFeatured ? "error" : "primary"}
                          startIcon={isFeatured ? <StarBorder /> : <Star />}
                          onClick={() => toggleFeaturedStatus(product.id, isFeatured)}
                        >
                          {isFeatured ? 'Remove' : 'Add'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFeaturedDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Details Dialog */}
      <Dialog
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Product Details</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <img
                    src={getProductImageUrl(selectedProduct)}
                    alt={selectedProduct.name}
                    style={{
                      width: '100%',
                      height: 200,
                      objectFit: 'cover',
                      borderRadius: 8
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {selectedProduct.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedProduct.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Chip label={selectedProduct.category} variant="outlined" />
                    <Chip 
                      label={selectedProduct.isFeatured ? "Featured" : "Not Featured"} 
                      color={selectedProduct.isFeatured ? "primary" : "default"}
                      icon={selectedProduct.isFeatured ? <Star /> : <StarBorder />}
                    />
                  </Box>
                  <Typography variant="h5" color="primary" sx={{ mb: 1 }}>
                    ₹{selectedProduct.price?.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Seller: {selectedProduct.sellerName}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDialogOpen(false)}>
            Close
          </Button>
          {selectedProduct && (
            <Button
              variant="contained"
              color={selectedProduct.isFeatured ? "error" : "primary"}
              startIcon={selectedProduct.isFeatured ? <StarBorder /> : <Star />}
              onClick={() => {
                toggleFeaturedStatus(selectedProduct.id, selectedProduct.isFeatured);
                setProductDialogOpen(false);
              }}
            >
              {selectedProduct.isFeatured ? 'Remove from Featured' : 'Add to Featured'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeaturedProductsTab;